import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, Clock, AlertCircle, User } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Props {
  onClose: () => void;
  onSubmitted: () => void;
}

export function CustomerOnboardingModal({ onClose, onSubmitted }: Props) {
  const [step, setStep]         = useState<"form" | "submitted">("form");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const [firstName,   setFirstName]   = useState("");
  const [lastName,    setLastName]    = useState("");
  const [phone,       setPhone]       = useState("");
  const [city,        setCity]        = useState("");
  const [address,     setAddress]     = useState("");
  const [idType,      setIdType]      = useState("Aadhaar");
  const [idNumber,    setIdNumber]    = useState("");
  const [description, setDescription] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim()) {
      setError("First name and last name are required.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/customer-profile`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, phone, city, address, idType, idNumber, description }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setError(data.error ?? "Submission failed."); return; }
      setStep("submitted");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={step === "submitted" ? onClose : undefined}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-sm bg-[#0d0b08] border border-[#d4af37]/30"
      >
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />

        <AnimatePresence mode="wait">
          {step === "form" ? (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-start justify-between p-6 pb-4 border-b border-white/5">
                <div>
                  <p className="text-[10px] tracking-[0.3em] text-[#d4af37]/60 uppercase mb-1">BookMySquadd — Verification</p>
                  <h2 className="text-xl font-serif text-gray-800">Complete Your Profile</h2>
                  <p className="text-sm text-[#9a8f83] mt-1">
                    A quick background check is required. Your account will be active after review.
                  </p>
                </div>
                <button onClick={onClose} className="p-1 text-gray-800/30 hover:text-gray-800/60 transition mt-1">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">First Name *</label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Last Name *</label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                  </div>
                </div>

                {/* Account Status */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Account Status</label>
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2.5">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-amber-300 font-medium">Under Review</span>
                    <span className="text-xs text-amber-400/60 ml-1">— Pending admin verification</span>
                  </div>
                </div>

                {/* Phone + City */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Phone Number</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 00000 00000"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">City</label>
                    <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Your city"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Address</label>
                  <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Full address"
                    className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                </div>

                {/* Govt ID */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">ID Type</label>
                    <select value={idType} onChange={e => setIdType(e.target.value)}
                      className="w-full bg-[#0d0b08] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#d4af37]/50 transition appearance-none"
                      style={{ colorScheme: "dark" }}>
                      {["Aadhaar", "PAN Card", "Passport", "Voter ID", "Driving License"].map(t => (
                        <option key={t} value={t} className="bg-[#0d0b08]">{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">ID Number</label>
                    <input type="text" value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder="Enter ID number"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition" />
                  </div>
                </div>

                {/* Additional info */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Additional Info <span className="normal-case tracking-normal text-[#9a8f83]/50">(optional)</span></label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                    placeholder="Anything else you'd like us to know..."
                    maxLength={500}
                    className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition resize-none" />
                  <p className="text-right text-xs text-[#9a8f83]/50 mt-1">{description.length}/500</p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-sm px-3 py-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2.5 text-sm text-[#9a8f83] border border-white/10 rounded-sm hover:border-white/20 transition">
                    Fill in later
                  </button>
                  <button type="submit" disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-[#d4af37] text-black font-semibold rounded-sm hover:bg-[#c9a227] transition disabled:opacity-60">
                    {loading ? <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <User className="w-4 h-4" />}
                    {loading ? "Submitting..." : "Submit for Verification"}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div key="submitted" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-[#d4af37]" />
              </div>
              <h2 className="text-xl font-serif text-gray-800 mb-2">Submitted for Review!</h2>
              <p className="text-sm text-[#9a8f83] mb-2">
                Your background check is now <strong className="text-amber-300">under review</strong>.
              </p>
              <p className="text-xs text-[#9a8f83]/70 mb-6">
                Our team will verify your details and you'll receive an email once approved. This usually takes 24–48 hours.
              </p>
              <button onClick={() => { onSubmitted(); onClose(); }}
                className="px-6 py-2.5 bg-[#d4af37] text-black text-sm font-semibold rounded-sm hover:bg-[#c9a227] transition">
                Got it, continue
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
